import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { navLinks } from '../data/mock';
import { MagneticButton, MagneticLink } from './MagneticElements';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleNavClick = (link) => {
    setIsMobileMenuOpen(false);
    
    if (link.isRoute) {
      navigate(link.href);
    } else {
      // If we're not on the homepage, navigate there first
      if (location.pathname !== '/') {
        navigate('/');
        // Wait for navigation then scroll
        setTimeout(() => {
          const element = document.querySelector(link.href);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth' });
          }
        }, 100);
      } else {
        const element = document.querySelector(link.href);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }
    }
  };

  const handleLogoClick = () => {
    if (location.pathname !== '/') {
      navigate('/');
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        isScrolled
          ? 'bg-black/90 backdrop-blur-xl border-b border-white/10'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <MagneticLink
            href="/"
            onClick={(e) => {
              e.preventDefault();
              handleLogoClick();
            }}
            strength={0.2}
            className="text-2xl font-semibold tracking-tight text-white hover:text-accent transition-colors duration-300"
          >
            <span style={{color: '#FF6B00'}}>W</span>ebysee<span className="text-accent">.</span>
          </MagneticLink>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <MagneticLink
                key={link.id}
                href={link.href}
                onClick={(e) => {
                  e.preventDefault();
                  handleNavClick(link);
                }}
                strength={0.25}
                className="text-white/60 hover:text-white text-sm font-medium transition-colors duration-300 relative group"
              >
                {link.label}
                <span className="absolute -bottom-1 left-0 w-0 h-px bg-accent transition-all duration-300 group-hover:w-full" />
              </MagneticLink>
            ))}
          </nav>

          {/* CTA Button - Premium Style */}
          <MagneticButton
            as="div"
            onClick={() => handleNavClick({ href: '#contact' })}
            strength={0.3}
            className="hidden md:block cursor-pointer"
          >
            <button
              onClick={() => handleNavClick({ href: '#contact' })}
              className="relative overflow-hidden px-6 py-3 text-sm font-medium text-black bg-accent group"
            >
              {/* Shine effect */}
              <span className="absolute inset-0 overflow-hidden">
                <span className="absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-700 ease-out bg-gradient-to-r from-transparent via-white/30 to-transparent skew-x-12" />
              </span>
              <span className="relative z-10">Demander un devis</span>
            </button>
          </MagneticButton>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="md:hidden text-white p-2"
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <div
        className={`md:hidden absolute top-20 left-0 right-0 bg-black/95 backdrop-blur-xl border-b border-white/10 transition-all duration-500 ${
          isMobileMenuOpen ? 'opacity-100 visible' : 'opacity-0 invisible'
        }`}
      >
        <nav className="flex flex-col p-6 gap-4">
          {navLinks.map((link) => (
            <a
              key={link.id}
              href={link.href}
              onClick={(e) => {
                e.preventDefault();
                handleNavClick(link);
              }}
              className="text-white/70 hover:text-white text-lg font-medium py-2 transition-colors duration-300"
            >
              {link.label}
            </a>
          ))}
          <button
            onClick={() => handleNavClick({ href: '#contact' })}
            className="relative overflow-hidden bg-accent text-black px-6 py-3 text-center font-medium mt-4 group"
          >
            <span className="absolute inset-0 overflow-hidden">
              <span className="absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-700 ease-out bg-gradient-to-r from-transparent via-white/30 to-transparent skew-x-12" />
            </span>
            <span className="relative z-10">Demander un devis</span>
          </button>
        </nav>
      </div>
    </header>
  );
};

export default Header;
